/**
 * useJobTrainingArtifacts
 *
 * Phase 1 job-based training recommendations + Phase 2 contextual scoring
 * + Phase 3 engagement-aware ranking.
 *
 * 3-step keyed query: shop job IDs → catalog job IDs → artifact map → artifacts.
 * When ticketContext is provided, artifacts are re-ranked by keyword relevance
 * and engagement signals.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TrainingArtifact } from '@/types/training';
// DTC-L: route training-artifact freshness anchor through clock.
import { clock } from '@/utils/clock';
// BAT-A2: bound `.in('artifact_id', artifactIds)` clauses in
// fetchEngagementStats and fetchPensieveStats. Both helpers receive their
// artifactIds list from the caller (typically the artifact set returned
// by the prior catalog/job-mapping step). In a shop with a large
// imported training catalog (1000+ active artifacts) the unbounded
// IN(...) clause would land on training_effectiveness_events and
// training_effect_signals, both of which can be large append-only event
// tables; capping the per-chunk size keeps planner cost stable as those
// tables grow.
import { chunkInQuery } from '@/utils/chunkInQuery';
import {
  scoreAndSort,
  type TicketContext,
  type ScoreBreakdown,
  type EngagementStats,
  type PensieveStats,
  type FitmentRow,
  buildTicketTrainingRecommendationCandidate,
  rankTicketTrainingRecommendations,
} from '@/utils/trainingRelevanceScorer';
// Batch 18-G6 — ticket recommendation data bridge.
import type {
  RankedTrainingRecommendation,
  TrainingRecommendationCandidate,
  TrainingRecommendationCompletionSummary,
  TrainingRecommendationContext,
  TrainingRecommendationRequirementMatch,
  TrainingRecommendationTier,
} from '@/types/trainingRecommendations';

export type EmptyReason = 'no_jobs' | 'no_catalog_match' | 'no_training_mapped' | 'no_vehicle' | 'no_relevant_match';

// ─── Batch 18-G6 — Bridge options ────────────────────────────────────
//
// Optional context the caller can pass so the bridge can safely query
// `training_requirements` and the technician's completion summary.
//
// All fields are optional. Missing fields gate the corresponding query
// off — the bridge never over-claims (see G5 §9). Existing two-arg
// callers (no options) continue to work and see no behaviour change.
export interface UseJobTrainingArtifactsOptions {
  orgId?: string;
  shopId?: string;
  technicianProfileId?: string;
  userId?: string;
  ticketId?: string;
  roleKey?: string | null;
  teamId?: string | null;
  domainKey?: string | null;
  currentDate?: string;
  limit?: number;
}

// ─── Batch 18-G6 — Allowed projections ────────────────────────────────
//
// The exact column lists G5 §5 and §6 locked. Centralised so the
// G6 source-pin can grep for them and the hook stays explicit about
// which columns reach the wire. The F35 §7 raw / private field
// exclusion list is enforced indirectly: every column not on these
// projections is absent from the query path.
const REQUIREMENT_PROJECTION =
  'id,artifact_id,org_id,team_id,role_key,domain_key,required_by_date,requires_manager_verification,is_active';
const SESSION_PROJECTION =
  'id,artifact_id,technician_profile_id,manual_marked_complete_at,completion_status';
const VERIFICATION_PROJECTION =
  'id,artifact_id,technician_profile_id,verification_status,created_at';

// ─── Batch 18-G6 — Due-soon / overdue window ─────────────────────────
const DUE_SOON_WINDOW_DAYS = 30;

// ─── Batch 18-K107 — multi-signal fallback tuning ────────────────────
// When a ticket's jobs have no `training_artifact_job_map` row (e.g. the
// K104 catalog restore skipped that table), we score the active catalog by
// vehicle fitment + keyword/job context instead of returning empty. This
// caps how many active artifacts we score (the restored catalog is a few
// hundred rows) so the fallback stays bounded.
const FALLBACK_POOL_CAP = 1000;
// Minimum keyword-match score for a keyword-only candidate (no vehicle
// fitment) to be eligible in fallback mode — keeps weak single-word noise out.
const FALLBACK_KEYWORD_MIN = 2;

export interface JobTrainingResult {
  artifacts: TrainingArtifact[];
  isEmpty: boolean;
  reason: EmptyReason | null;
  scores?: Map<string, ScoreBreakdown>;
  engagementMap?: Map<string, EngagementStats>;
  pensieveMap?: Map<string, PensieveStats>;
  // Batch 18-G6 — optional ranked recommendations. Empty array when
  // the existing pipeline returns no artifacts; absent when the bridge
  // did not run (e.g. no `ticketContext`).
  rankedRecommendations?: RankedTrainingRecommendation[];
}

/** Simple hash for cache key — avoids full text in queryKey */
function hashContext(ctx: TicketContext | undefined): string {
  if (!ctx) return '';
  const raw = [
    ...ctx.jobNames,
    ...ctx.jobCategories,
    ctx.ticketNotes ?? '',
    ctx.customerRequestNotes ?? '',
  ].join('|');
  let h = 0;
  for (let i = 0; i < raw.length; i++) {
    h = ((h << 5) - h + raw.charCodeAt(i)) | 0;
  }
  return String(h);
}

/**
 * Fetch engagement stats for a set of artifact IDs.
 * Global scope (all techs, all shops) — stronger signal with current data volume.
 * "Completion" = engagement depth, NOT diagnostic success.
 *
 * FIX-R114-MED-3: bound the lookup to the recent 90 days. The previous
 * unbounded query aggregated all-time engagement, which (a) has unbounded
 * scan cost as the events table grows and (b) lets ancient engagement
 * dominate "current relevance" — an artifact opened heavily two years ago
 * but ignored since should not outrank one with recent traction. 90 days
 * is consistent with the DECAY_DAYS window used in process-training-signals.
 */
const ENGAGEMENT_LOOKBACK_DAYS = 90;

/** Rows of the anonymised server aggregate (Core-100 R4 wave 3, CORE-O). */
export interface EngagementTotalsRow {
  artifact_id: string;
  unique_openers: number;
  completion_count: number;
  last_used_at: string | null;
}

/**
 * Core-100 R4 wave 3 (CORE-O D): engagement is a PLATFORM-WIDE total, so it is
 * computed on the server by `get_training_artifact_engagement_totals`
 * (migration 20270206024000), never from raw learner events in the browser.
 * The raw `training_effectiveness_events` are the learner's own history and
 * are readable only by the learner and their current managers
 * (20270206021800), so a client-side aggregation would silently count only
 * the caller's own events. The aggregate applies a privacy floor (an artifact
 * appears only when at least 5 distinct technicians engaged with it) and
 * returns no technician identity; below the floor there is no row, which the
 * ranking treats exactly like "no engagement signal".
 *
 * Engagement is an optional ranking input: a failed read is logged and the
 * list is ranked without it (unchanged behaviour).
 */
export async function fetchEngagementStats(artifactIds: string[]): Promise<Map<string, EngagementStats>> {
  const map = new Map<string, EngagementStats>();
  if (artifactIds.length === 0) return map;

  // DTC-L: the window anchor comes from the app clock (the server clamps it to
  // at most 365 days back and never into the future).
  const since = clock.now();
  since.setDate(since.getDate() - ENGAGEMENT_LOOKBACK_DAYS);

  // BAT-A2: still chunked, so one call never carries an unbounded id list
  // (the server refuses more than 1000 ids per call).
  let res: { data: EngagementTotalsRow[]; error: unknown };
  try {
    res = await chunkInQuery<string, EngagementTotalsRow>(artifactIds, (chunk) =>
      supabase.rpc('get_training_artifact_engagement_totals' as never, {
        p_artifact_ids: chunk,
        p_since: since.toISOString(),
      } as never) as unknown as PromiseLike<{ data: EngagementTotalsRow[] | null; error: unknown }>,
    );
  } catch (e) {
    console.warn('[EngagementStats] Query failed:', e instanceof Error ? e.message : String(e));
    return map;
  }

  if (res.error) {
    const msg = (res.error as { message?: string } | null)?.message ?? String(res.error);
    console.warn('[EngagementStats] Query failed:', msg);
    return map;
  }

  for (const row of res.data ?? []) {
    map.set(row.artifact_id, {
      uniqueOpeners: Number(row.unique_openers) || 0,
      completionCount: Number(row.completion_count) || 0,
      lastUsedAt: row.last_used_at ?? null,
    });
  }

  return map;
}

/**
 * Fetch Pensieve stats (behavioral observation signals) for a set of artifact IDs.
 * Aggregates training_effect_signals per artifact: positive count, total samples, avg confidence.
 * These are behavioral observations, NOT success indicators.
 */
async function fetchPensieveStats(artifactIds: string[]): Promise<Map<string, PensieveStats>> {
  const map = new Map<string, PensieveStats>();
  if (artifactIds.length === 0) return map;

  // BAT-A2: chunk on artifactIds for the same reason as
  // fetchEngagementStats above — training_effect_signals is an append-
  // only behavioral-observation table whose IN(...) clause cost grows
  // with both the artifact list size AND the table's row count.
  const res = await chunkInQuery<
    string,
    {
      artifact_id: string;
      direction: string;
      confidence_weight: number | null;
      sample_size: number | null;
    }
  >(artifactIds, (chunk) =>
    supabase
      .from('training_effect_signals')
      .select('artifact_id, direction, confidence_weight, sample_size')
      .in('artifact_id', chunk),
  );
  const data = res.data;
  const error = res.error;

  if (error) {
    const msg = (error as { message?: string } | null)?.message ?? String(error);
    console.warn('[PensieveStats] Query failed:', msg);
    return map;
  }

  if (!data || data.length === 0) return map;

  // Aggregate per artifact
  const agg = new Map<string, {
    positiveCount: number;
    totalSamples: number;
    confidenceSum: number;
    rowCount: number;
  }>();

  for (const row of data) {
    const aid = row.artifact_id;
    if (!agg.has(aid)) {
      agg.set(aid, { positiveCount: 0, totalSamples: 0, confidenceSum: 0, rowCount: 0 });
    }
    const entry = agg.get(aid)!;

    if (row.direction === 'positive') {
      entry.positiveCount += 1;
    }
    entry.totalSamples += row.sample_size ?? 0;
    entry.confidenceSum += row.confidence_weight ?? 0;
    entry.rowCount += 1;
  }

  for (const [aid, entry] of agg) {
    map.set(aid, {
      positiveSignals: entry.positiveCount,
      totalSamples: entry.totalSamples,
      avgConfidence: entry.rowCount > 0 ? entry.confidenceSum / entry.rowCount : 0,
    });
  }

  return map;
}

// =====================================================================
// Batch 18-G6 — Bridge helpers (pure, locally scoped)
// =====================================================================

interface RequirementRowLike {
  id: string;
  artifact_id: string | null;
  org_id: string;
  team_id: string | null;
  role_key: string | null;
  domain_key: string | null;
  required_by_date: string | null;
  requires_manager_verification: boolean;
  is_active: boolean | null;
}

interface SessionRowLike {
  id: string;
  artifact_id: string | null;
  technician_profile_id: string | null;
  manual_marked_complete_at: string | null;
  completion_status: string | null;
}

interface VerificationRowLike {
  id: string;
  artifact_id: string | null;
  technician_profile_id: string | null;
  verification_status: 'verified' | 'revoked';
  created_at: string;
}

/**
 * Convert a raw `training_requirements` row + `currentDate` into a
 * `TrainingRecommendationRequirementMatch`. The `isOverdue` /
 * `isDueSoon` flags are computed locally so the ranker stays pure.
 */
function toRequirementMatch(
  row: RequirementRowLike,
  currentDate: Date,
): TrainingRecommendationRequirementMatch {
  const due = row.required_by_date ? new Date(row.required_by_date) : null;
  const dueSoonCutoff = new Date(currentDate);
  dueSoonCutoff.setDate(dueSoonCutoff.getDate() + DUE_SOON_WINDOW_DAYS);

  const isOverdue = !!due && due.getTime() < currentDate.getTime();
  const isDueSoon =
    !!due &&
    !isOverdue &&
    due.getTime() <= dueSoonCutoff.getTime();

  return {
    requirementId: row.id,
    requiresManagerVerification: row.requires_manager_verification,
    requiredByDate: row.required_by_date ?? null,
    roleKey: row.role_key ?? null,
    teamId: row.team_id ?? null,
    domainKey: row.domain_key ?? null,
    isOverdue,
    isDueSoon,
  };
}

/**
 * Pure completion-summary derivation for a single (artifact,
 * technician) pair. Mirrors the F32 / F36 helper precedence:
 *
 *   1. latest verification row is `'revoked'` → `'revoked'`
 *   2. any verification row is `'verified'` (not superseded by a
 *      later revocation per step 1) → `'verified'`
 *   3. manual completion exists AND a matching requirement has
 *      `requires_manager_verification = true` → `'verification_pending'`
 *   4. manual completion exists → `'manual_submitted'`
 *   5. otherwise → `'none'`
 *
 * F32 uses `'no_evidence'` for the zero case; the G3 contract uses
 * `'none'`. The semantic is identical.
 */
function deriveCompletionSummary(
  sessions: SessionRowLike[],
  verifications: VerificationRowLike[],
  requiresManagerVerification: boolean,
): TrainingRecommendationCompletionSummary {
  // Sort verifications newest-first (lexical ISO compare matches
  // chronological).
  const sortedVerifications = [...verifications].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );

  const latestVerification = sortedVerifications[0];
  const anyVerified = sortedVerifications.some(
    (v) => v.verification_status === 'verified',
  );

  const manualCompletions = sessions.filter(
    (s) => !!s.manual_marked_complete_at,
  );
  const hasManual = manualCompletions.length > 0;

  const lastCompletedAt = hasManual
    ? manualCompletions
        .map((s) => s.manual_marked_complete_at)
        .filter((v): v is string => !!v)
        .sort((a, b) => b.localeCompare(a))[0] ?? null
    : null;
  const lastVerifiedAt =
    sortedVerifications.find((v) => v.verification_status === 'verified')
      ?.created_at ?? null;
  const lastRevokedAt =
    sortedVerifications.find((v) => v.verification_status === 'revoked')
      ?.created_at ?? null;

  let status: TrainingRecommendationCompletionSummary['status'] = 'none';
  if (latestVerification?.verification_status === 'revoked') {
    status = 'revoked';
  } else if (anyVerified) {
    status = 'verified';
  } else if (hasManual && requiresManagerVerification) {
    status = 'verification_pending';
  } else if (hasManual) {
    status = 'manual_submitted';
  }

  return {
    status,
    lastCompletedAt,
    lastVerifiedAt,
    lastRevokedAt,
    requiresManagerVerification,
  };
}

export function useJobTrainingArtifacts(
  shopJobIds: string[],
  ticketContext?: TicketContext,
  options?: UseJobTrainingArtifactsOptions,
) {
  // Batch 18-G6 — single canonical resolved org id for the bridge.
  const requirementOrgId = options?.orgId ?? options?.shopId;
  return useQuery<JobTrainingResult>({
    queryKey: [
      'job-training-artifacts',
      ...shopJobIds.sort(),
      hashContext(ticketContext),
      // Batch 18-G6 — options that affect query results join the
      // cache key. Fields that only affect the post-rank context
      // (e.g. `userId`, `roleKey` when not used for filtering) still
      // participate because a future batch may light them up.
      requirementOrgId ?? '',
      options?.technicianProfileId ?? '',
      options?.userId ?? '',
      options?.ticketId ?? '',
      options?.roleKey ?? '',
      options?.teamId ?? '',
      options?.domainKey ?? '',
      options?.currentDate ?? '',
      options?.limit ?? 0,
    ],
    queryFn: async (): Promise<JobTrainingResult> => {
      // Gate: no jobs
      if (shopJobIds.length === 0) {
        return { artifacts: [], isEmpty: true, reason: 'no_jobs' };
      }

      // Gate: YMME required — all four vehicle fields must be present
      if (
        !ticketContext?.vehicleYear ||
        !ticketContext?.vehicleMake ||
        !ticketContext?.vehicleModel ||
        !ticketContext?.vehicleEngine
      ) {
        return { artifacts: [], isEmpty: true, reason: 'no_vehicle' };
      }

      // Step 1: Resolve shop job IDs → catalog job IDs
      const { data: jobRows, error: jobErr } = await supabase
        .from('jobs')
        .select('catalog_job_id')
        .in('id', shopJobIds)
        .not('catalog_job_id', 'is', null);

      if (jobErr) throw jobErr;

      const catalogJobIds = [...new Set(
        (jobRows || [])
          .map(r => r.catalog_job_id as string)
          .filter(Boolean)
      )];

      if (catalogJobIds.length === 0) {
        return { artifacts: [], isEmpty: true, reason: 'no_catalog_match' };
      }

      // Step 2: Resolve catalog job IDs → explicit job-map artifact IDs.
      const { data: mapRows, error: mapErr } = await supabase
        .from('training_artifact_job_map')
        .select('artifact_id')
        .in('job_id', catalogJobIds);

      if (mapErr) throw mapErr;

      const explicitArtifactIds = [...new Set(
        (mapRows || []).map(r => r.artifact_id as string)
      )];

      // Batch 18-K107 — multi-signal fallback. When there is no explicit
      // job-map row (e.g. the K104 catalog restore intentionally skipped
      // `training_artifact_job_map`), do NOT bail out with `no_training_mapped`:
      // score the active catalog by vehicle fitment + keyword/job context so a
      // restored artifact that clearly matches the ticket (correct YMM fitment
      // + relevant keywords) can still be recommended. Explicit job-map matches
      // keep priority — their path below is unchanged.
      const recommendationMode: 'job_map' | 'fallback' =
        explicitArtifactIds.length > 0 ? 'job_map' : 'fallback';

      // Step 3: fetch the candidate artifact set.
      const artResult =
        recommendationMode === 'job_map'
          ? await supabase
              .from('training_artifacts')
              .select('*')
              .in('id', explicitArtifactIds)
              .eq('is_active', true)
          : await supabase
              .from('training_artifacts')
              .select('*')
              .eq('is_active', true)
              .order('id')
              .limit(FALLBACK_POOL_CAP);

      if (artResult.error) throw artResult.error;

      const fetched = (artResult.data || []) as TrainingArtifact[];

      if (fetched.length === 0) {
        return { artifacts: [], isEmpty: true, reason: 'no_training_mapped' };
      }

      // Candidate ids drive the engagement / pensieve / fitment lookups.
      const artifactIds = fetched.map((a) => a.id);

      // Fitment is a small table; in fallback mode fetch it whole (no oversized
      // IN(...) URL for a few-hundred-id candidate set) and let the client-side
      // map filter to the candidates. Engagement/pensieve already chunk their IN.
      const [engagementMap, pensieveMap, fitmentResult] = await Promise.all([
        fetchEngagementStats(artifactIds),
        fetchPensieveStats(artifactIds),
        recommendationMode === 'job_map'
          ? supabase
              .from('training_artifact_fitment')
              .select('artifact_id, year, make, model, engine, boost')
              .in('artifact_id', artifactIds)
          : supabase
              .from('training_artifact_fitment')
              .select('artifact_id, year, make, model, engine, boost'),
      ]);

      // Build fitment map: artifact_id → FitmentRow[]
      const fitmentMap = new Map<string, FitmentRow[]>();
      if (fitmentResult.data) {
        for (const row of fitmentResult.data) {
          const aid = row.artifact_id;
          if (!fitmentMap.has(aid)) fitmentMap.set(aid, []);
          fitmentMap.get(aid)!.push(row as FitmentRow);
        }
      }

      // Batch 18-G6 — Build the recommendation bridge once for either
      // return path. The bridge is pure and only fires its DB reads
      // when the corresponding context fields are present (G5 §9).
      const buildBridge = async (
        finalArtifacts: TrainingArtifact[],
        scoreMap: Map<string, ScoreBreakdown> | undefined,
      ): Promise<RankedTrainingRecommendation[] | undefined> => {
        const artifactIds = finalArtifacts.map((a) => a.id);
        if (artifactIds.length === 0) return [];

        // (a) Requirement match query — gated on org / shop context.
        const requirementMatchesByArtifact = new Map<
          string,
          TrainingRecommendationRequirementMatch[]
        >();
        const currentDate = options?.currentDate
          ? new Date(options.currentDate)
          : clock.now();
        if (requirementOrgId) {
          const { data: reqRows, error: reqErr } = await supabase
            .from('training_requirements')
            .select(REQUIREMENT_PROJECTION)
            .eq('is_active', true)
            .in('artifact_id', artifactIds)
            .eq('org_id', requirementOrgId);
          if (reqErr) {
            // Soft-fail: log and continue without requirement matches
            // so the existing artifact list still renders.
            const msg =
              (reqErr as { message?: string } | null)?.message ??
              String(reqErr);
            console.warn('[G6 bridge] requirements query failed:', msg);
          } else if (reqRows) {
            for (const row of reqRows as RequirementRowLike[]) {
              if (!row.artifact_id) continue;
              const match = toRequirementMatch(row, currentDate);
              const list =
                requirementMatchesByArtifact.get(row.artifact_id) ?? [];
              list.push(match);
              requirementMatchesByArtifact.set(row.artifact_id, list);
            }
          }
        }

        // (b) Completion-summary derivation — gated on technician id.
        const completionSummaryByArtifact = new Map<
          string,
          TrainingRecommendationCompletionSummary
        >();
        if (options?.technicianProfileId) {
          const [sessionsRes, verificationsRes] = await Promise.all([
            supabase
              .from('training_consumption_sessions')
              .select(SESSION_PROJECTION)
              .eq('technician_profile_id', options.technicianProfileId)
              .in('artifact_id', artifactIds),
            supabase
              .from('training_completion_verifications')
              .select(VERIFICATION_PROJECTION)
              .eq('technician_profile_id', options.technicianProfileId)
              .in('artifact_id', artifactIds),
          ]);
          const sessionsByArtifact = new Map<string, SessionRowLike[]>();
          const verificationsByArtifact = new Map<
            string,
            VerificationRowLike[]
          >();
          if (sessionsRes.data) {
            for (const row of sessionsRes.data as SessionRowLike[]) {
              if (!row.artifact_id) continue;
              const list = sessionsByArtifact.get(row.artifact_id) ?? [];
              list.push(row);
              sessionsByArtifact.set(row.artifact_id, list);
            }
          }
          if (verificationsRes.data) {
            for (const row of verificationsRes.data as VerificationRowLike[]) {
              if (!row.artifact_id) continue;
              const list = verificationsByArtifact.get(row.artifact_id) ?? [];
              list.push(row);
              verificationsByArtifact.set(row.artifact_id, list);
            }
          }
          for (const artifactId of artifactIds) {
            const matches =
              requirementMatchesByArtifact.get(artifactId) ?? [];
            const requiresMv = matches.some(
              (m) => m.requiresManagerVerification === true,
            );
            const summary = deriveCompletionSummary(
              sessionsByArtifact.get(artifactId) ?? [],
              verificationsByArtifact.get(artifactId) ?? [],
              requiresMv,
            );
            completionSummaryByArtifact.set(artifactId, summary);
          }
        }

        // (c) Build context.
        const context: TrainingRecommendationContext = {
          surface: 'ticket',
          orgId: requirementOrgId,
          shopId: options?.shopId,
          technicianProfileId: options?.technicianProfileId,
          userId: options?.userId,
          roleKey: options?.roleKey ?? null,
          teamId: options?.teamId ?? null,
          domainKey: options?.domainKey ?? null,
          ticketId: options?.ticketId ?? null,
          ticketJobIds: shopJobIds,
          ticketJobNames: ticketContext?.jobNames ?? [],
          ticketJobCategories: ticketContext?.jobCategories ?? [],
          concernText: [
            ticketContext?.ticketNotes,
            ticketContext?.customerRequestNotes,
          ]
            .filter((s): s is string => !!s)
            .join('\n') || null,
          dtcCodes: [],
          symptomTokens: [],
          vehicle: {
            year: ticketContext?.vehicleYear ?? null,
            make: ticketContext?.vehicleMake ?? null,
            model: ticketContext?.vehicleModel ?? null,
            engine: ticketContext?.vehicleEngine ?? null,
          },
          currentDate: (options?.currentDate ?? clock.now().toISOString()),
          limit: options?.limit,
        };

        // (d) Build candidates + rank.
        const inputs = finalArtifacts.map((artifact) => {
          const breakdown = scoreMap?.get(artifact.id);
          const intraTierScore = breakdown?.total ?? 0;
          const candidate: TrainingRecommendationCandidate =
            buildTicketTrainingRecommendationCandidate({
              artifactId: artifact.id,
              title: artifact.title,
              isActive: artifact.is_active ?? true,
              description: artifact.original_summary_short ?? null,
              source: {
                provider: artifact.provider_name ?? null,
                creator: artifact.instructor_name ?? null,
                accessType:
                  artifact.access_type === 'free'
                    ? 'free'
                    : artifact.access_type === 'paid'
                      ? 'paid'
                      : 'unknown',
                artifactType: artifact.artifact_type ?? null,
              },
              requirementMatches:
                requirementMatchesByArtifact.get(artifact.id) ?? undefined,
              completionSummary:
                completionSummaryByArtifact.get(artifact.id) ?? undefined,
              engagementSignals: breakdown
                ? {
                    uniqueOpens90d: engagementMap.get(artifact.id)?.uniqueOpeners,
                    completions90d: engagementMap.get(artifact.id)?.completionCount,
                  }
                : undefined,
              fitment:
                breakdown && breakdown.fitment > 0
                  ? { fitsYearMakeModelEngine: true, fitmentScore: breakdown.fitment }
                  : breakdown
                    ? { fitsYearMakeModelEngine: false, fitmentScore: 0 }
                    : undefined,
            });

          // Base tier: exact ticket match when the scorer's
          // job/category signal fires (category > 0 or keyword > 0)
          // and the YMME gate passed. Otherwise related_helpful so
          // the candidate still ranks above general_fallback.
          let baseTier: TrainingRecommendationTier = 'related_helpful';
          if (breakdown && breakdown.fitment > 0) {
            const hasTicketSignal =
              breakdown.category > 0 || breakdown.keyword > 0;
            baseTier = hasTicketSignal ? 'exact_ticket_match' : 'related_helpful';
          }

          return {
            candidate,
            intraTierScore,
            baseTier,
          };
        });

        let ranked = rankTicketTrainingRecommendations(context, inputs);
        if (typeof options?.limit === 'number' && options.limit >= 0) {
          ranked = ranked.slice(0, options.limit);
        }
        return ranked;
      };

      // Phase 2+3+4+5+6: Apply contextual scoring + engagement + pensieve + fitment
      if (ticketContext) {
        const { sorted, scores } = scoreAndSort(fetched, ticketContext, engagementMap, pensieveMap, fitmentMap);

        // Eligibility gate.
        //  - job_map mode: preserve the existing YMME gate (fitment > 0).
        //  - fallback mode: require meaningful signal so the tab is never
        //    spammed — either a vehicle fitment match WITH some job/keyword
        //    relevance, or a strong keyword-only match (which ranks lower via
        //    its total score). Vehicle-only (fitment but no job/keyword
        //    relevance) is intentionally excluded to avoid noisy false
        //    positives.
        const eligible = sorted.filter((a) => {
          const b = scores.get(a.id);
          if (!b) return false;
          if (recommendationMode === 'job_map') {
            return b.fitment > 0;
          }
          const hasVehicle = b.fitment > 0;
          const hasRelevance = b.keyword > 0 || b.category > 0;
          return (hasVehicle && hasRelevance) || b.keyword >= FALLBACK_KEYWORD_MIN;
        });

        const rankedRecommendations = await buildBridge(eligible, scores);

        return {
          artifacts: eligible,
          isEmpty: eligible.length === 0,
          reason:
            eligible.length === 0
              ? recommendationMode === 'fallback'
                ? 'no_relevant_match'
                : 'no_training_mapped'
              : null,
          scores,
          engagementMap,
          pensieveMap,
          rankedRecommendations,
        };
      }

      // No ticket context to score against. In fallback mode that means no
      // usable signal at all (no job map, no job/keyword/notes) — return an
      // honest empty state rather than dumping the whole active catalog.
      if (recommendationMode === 'fallback') {
        return { artifacts: [], isEmpty: true, reason: 'no_relevant_match' };
      }

      // Phase 1 (job_map mode, no ticket context): artifact_type asc, then title asc
      fetched.sort((a, b) => {
        const typeCmp = a.artifact_type.localeCompare(b.artifact_type);
        if (typeCmp !== 0) return typeCmp;
        return a.title.localeCompare(b.title);
      });

      const rankedRecommendations = await buildBridge(fetched, undefined);

      return {
        artifacts: fetched,
        isEmpty: false,
        reason: null,
        engagementMap,
        pensieveMap,
        rankedRecommendations,
      };
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
  });
}
